import { QueryClient, UseQueryOptions } from '@tanstack/react-query'
import Routes from '../../routes'
import {
  FetcherError,
  MemberJobApplicationsQuery,
  MemberJobApplicationsQueryVariables as MemberJobApplicationsVars,
  useMemberJobApplicationsQuery as useEndpoint,
} from '../types'

type MemberJobApplicationsOptions = UseQueryOptions<
  MemberJobApplicationsQuery,
  FetcherError,
  MemberJobApplicationsQuery
>

/**
 * @private
 * Retrieve the `MemberJobApplications` query key used in the internal cache.
 */
const getKey = useEndpoint.getKey

// -------------------------------------

/**
 * Convenience wrapper around react-query's `invalidateQueries` function. If no
 * variables are passed all `MemberJobApplications` queries will be
 * invalidated.
 *
 * @see https://react-query.tanstack.com/guides/query-invalidation
 *
 * @example
 * invalidateMemberJobApplications(queryClient, { displayname: 'moontower' })
 * invalidateMemberJobApplications(queryClient)
 */
function invalidateMemberJobApplications(
  queryClient: QueryClient,
  variables?: MemberJobApplicationsVars,
) {
  queryClient.invalidateQueries(
    variables ? getKey(variables) : ['MemberJobApplications'],
  )
}

/**
 * Fetch a `MemberJobApplicationsFragment`.
 * Passing `initialData` will hydrate the cache.
 *
 * @example
 * const { data, uri } = useMemberJobApplications({ id: jobPosting.id })
 * const { data, uri } = useMemberJobApplications({ id: node.id }, { initialData: node })
 *
 * @TODO This endpoint should change shortly to support pagination
 * @TODO Add `optimisticCreate|Update|Delete`
 * @TODO Support `prefetchMemberJobApplications`?
 * @TODO Derive data in `select`
 * @TODO Tune options
 */
function useMemberJobApplications(
  variables: MemberJobApplicationsVars,
  options?: MemberJobApplicationsOptions,
) {
  const query = useEndpoint(variables, {
    enabled: options?.initialData == null,
    ...options,
  })
  const data =
    (options?.initialData as MemberJobApplicationsQuery) || query?.data
  const uri = Routes.MEMBERS_APPLICATIONS.replace(
    ':username',
    variables.username,
  )
  return { ...query, data, uri }
}

// -------------------------------------

export type { MemberJobApplicationsOptions, MemberJobApplicationsVars }
export { invalidateMemberJobApplications }
export default useMemberJobApplications
